import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Col } from "./grid/Col"
import IconArrow from "../icon/IconArrow"
import { ClickSoak } from ".."
import { Button } from "devextreme-react"
import { t } from "i18next"

interface CollapsibleProps {
    collapsed?: boolean
    children: React.ReactNode
    width?: number
    icon?: string
    closeDropdownOnItemClick?: boolean
}

export default function Collapsible(props: CollapsibleProps) {
    const [closing, setClosing] = useState(false)
    const [open, setOpen] = useState(false)
    const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
    const buttonRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect()
            const dropdownWidth = props.width ?? 200
            const dropdownHeight = 200
            const viewportWidth = window.innerWidth
            const viewportHeight = window.innerHeight

            let top = rect.bottom + window.scrollY + 4
            let left = rect.left + window.scrollX + 80

            if (left + dropdownWidth > viewportWidth) {
                left = viewportWidth - dropdownWidth - 8
            }

            if (top + dropdownHeight > viewportHeight + window.scrollY) {
                top = rect.top + window.scrollY - dropdownHeight - 4
            }

            setCoords({ top, left })
        }
    }, [open, props.width])

    useEffect(() => {
        const handleClickOutside = (e: Event) => {
            if (
                open &&
                buttonRef.current &&
                dropdownRef.current &&
                !buttonRef.current.contains(e.target as Node) &&
                !dropdownRef.current.contains(e.target as Node)
            ) {
                handleClose()
            }
        }

        document.addEventListener("mousedown", handleClickOutside)
        document.addEventListener("scroll", handleClickOutside, true)
        window.addEventListener("resize", handleClickOutside)

        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
            document.removeEventListener("scroll", handleClickOutside, true)
            window.removeEventListener("resize", handleClickOutside)
        }
    }, [open])

    const handleItemClick = () => {
        if (props.closeDropdownOnItemClick) {
            handleClose()
        }
    }

    const handleClose = () => {
        setClosing(true);
        setTimeout(() => {
            setOpen(false);
            setClosing(false);
        }, 250); // same as animation duration
    };


    if (!props.collapsed) return <>{props.children}</>

    return (
        <>
            <div
                ref={buttonRef}
                style={{ display: "inline-flex", cursor: "pointer" }}
                onClick={() => setOpen((prev) => !prev)}>
                {props.icon && <span>{props.icon}</span>}
                <Button height={32}>
                    <>
                        {t("Actions")}
                        <IconArrow direction={open && !closing ? "up" : "down"} />
                    </>
                </Button>
            </div>

            {open &&
                createPortal(
                    <>
                        <ClickSoak />
                        <div
                            ref={dropdownRef}
                            className={`confirm-dropdown ${closing ? "close" : open ? "open" : ""}`}
                            style={{
                                top: coords.top,
                                left: coords.left + 20,
                                width: props.width ?? 200,
                                backgroundColor: "rgba(255, 255, 255, 0.8)",
                                backdropFilter: "blur(10px)",
                                zIndex: 3000
                            }}
                            onClick={(e) => e.stopPropagation()}>
                            <div onClick={handleItemClick}>
                                <Col gap={"0.5"}>{props.children}</Col>
                            </div>
                        </div>
                    </>,
                    document.body
                )}
        </>
    )
}
